import { ChevronRight, MessageSquare, Zap, Bot, CreditCard, LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface NavItem {
  value: string
  label: string
  icon: LucideIcon
  tooltip: string
}

const navItems: NavItem[] = [
  { value: 'chat', label: 'Playground', icon: MessageSquare, tooltip: 'Playground' },
  { value: 'plan', label: 'Overview', icon: Zap, tooltip: 'Overview' },
  { value: 'tools', label: 'Tools', icon: Bot, tooltip: 'Tools' },
  { value: 'usage', label: 'Billing', icon: CreditCard, tooltip: 'Billing' },
]

interface CollapsedSidebarStripProps {
  onToggle: () => void
  activeView?: string
  setActiveView?: (view: string) => void
}

/**
 * Collapsed sidebar strip - shows when sidebar is fully collapsed.
 * Shows navigation icons for quick access and a toggle button to expand.
 */
export function CollapsedSidebarStrip({
  onToggle,
  activeView,
  setActiveView,
}: CollapsedSidebarStripProps) {
  return (
    <div className="relative w-12 h-full bg-background border-r flex flex-col">
      {/* Navigation icons */}
      <nav className="flex-1 flex flex-col items-center pt-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = activeView === item.value

          return (
            <Tooltip key={item.value} delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setActiveView?.(item.value)}
                  className={cn(
                    'w-10 h-10 flex items-center justify-center rounded-lg transition-all',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Icon className={cn('w-5 h-5', isActive && 'text-primary')} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                <p>{item.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )
        })}
      </nav>

      {/* Expand button at bottom */}
      <div className="flex-shrink-0 pb-4 flex justify-center">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              onClick={onToggle}
              className={cn(
                'w-10 h-10 flex items-center justify-center rounded-lg',
                'text-muted-foreground hover:bg-muted hover:text-foreground',
                'transition-all',
              )}
              title="Expand sidebar"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <p>Expand sidebar</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

// Re-export for backwards compatibility (SidebarResizeHandle is no longer needed with Allotment)
export { CollapsedSidebarStrip as SidebarResizeHandle }
